import React, { useState } from 'react';
import { Button, Drawer, Dropdown, Form, Popconfirm, Segmented, Space, theme, Tooltip, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, CheckCircleFilled, CloseCircleFilled,
  CopyOutlined, DeleteOutlined, EditOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TGDDesign, TGDRule, TGDRuleType, TGDTier } from '../../../models/courses/graphicDesign';
import { TGDEvaluation } from '../core/engine';
import RuleParamsForm from './RuleParamsForm';
import LocalizedInput, { TAdminLanguage } from './LocalizedInput';
import { createDefaultRule, RULE_TYPE_HELP_FR, RULE_TYPE_LABELS_FR } from './ruleTemplates';

interface Props { rules: TGDRule[]; onChange: (rules: TGDRule[]) => void;
  allowedTypes: TGDRuleType[]; solution?: TGDDesign; evaluation?: TGDEvaluation;
  zoneIds?: string[]; versionIds?: string[]; choiceIds?: string[]; readOnly?: boolean;
  language: TAdminLanguage }
const tiers: TGDTier[] = ['required', 'constraint', 'bonus'];
const RulesEditor: React.FC<Props> = ({ rules, onChange, allowedTypes, solution,
  evaluation, zoneIds, versionIds, choiceIds, readOnly, language }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [selected, setSelected] = useState<string | null>(null);
  const current = rules.find(rule => rule.id === selected);
  const replace = (next: TGDRule) => onChange(rules.map(rule => rule.id === next.id ? next : rule));
  const move = (rule: TGDRule, offset: number) => {
    const group = rules.filter(item => item.tier === rule.tier);
    const target = group[group.findIndex(item => item.id === rule.id) + offset];
    if (!target) return;
    const next = [...rules];
    const a = next.findIndex(item => item.id === rule.id);
    const b = next.findIndex(item => item.id === target.id);
    [next[a], next[b]] = [next[b], next[a]];
    onChange(next);
  };
  const add = (type: TGDRuleType, tier: TGDTier) => {
    const next = { ...createDefaultRule(type, rules.map(rule => rule.id)), tier };
    onChange([...rules, next]); setSelected(next.id);
  };
  const icon = (rule: TGDRule) => {
    const result = evaluation?.results.find(item => item.ruleId === rule.id);
    const label = t(`graphicDesignLesson.admin.${result ? result.passed
      ? 'modelPasses' : 'modelFails' : 'noModel'}`);
    return <Tooltip title={label}><span aria-label={label}>{result
      ? result.passed ? <CheckCircleFilled style={{ color: token.colorSuccess }} />
        : <CloseCircleFilled style={{ color: token.colorError }} />
      : <MinusCircleOutlined style={{ color: token.colorTextSecondary }} />}</span></Tooltip>;
  };
  const action = (title: string, icon: React.ReactNode, onClick: () => void,
    disabled = false) => <Tooltip title={title}><Button type="text" icon={icon}
      aria-label={title} disabled={disabled} onClick={onClick} /></Tooltip>;
  return <>
    {tiers.map(tier => {
      const group = rules.filter(rule => rule.tier === tier);
      return <section key={tier} style={{ marginBottom: 24 }}>
        <h4>{t(`graphicDesignLesson.admin.tiers.${tier}`)}</h4>
        <Typography.Text type="secondary">{t(`graphicDesignLesson.admin.tierHelp.${tier}`)}</Typography.Text>
        {group.length ? group.map((rule, index) => <div className="gd-admin-row" key={rule.id}>
          {icon(rule)}<div style={{ flex: 1, minWidth: 0 }}>
            <div>{rule.label[language] || rule.label.fr || RULE_TYPE_LABELS_FR[rule.type]}</div>
            <Typography.Text type="secondary">{RULE_TYPE_LABELS_FR[rule.type]}</Typography.Text>
          </div>
          {action(t('graphicDesignLesson.admin.edit'), <EditOutlined />,
            () => setSelected(rule.id))}
          {action(t('graphicDesignLesson.admin.moveUp'), <ArrowUpOutlined />,
            () => move(rule, -1), readOnly || index === 0)}
          {action(t('graphicDesignLesson.admin.moveDown'), <ArrowDownOutlined />,
            () => move(rule, 1), readOnly || index === group.length - 1)}
          {action(t('graphicDesignLesson.admin.duplicate'), <CopyOutlined />,
            () => { const copy = createDefaultRule(rule.type, rules.map(item => item.id));
              const position = rules.findIndex(item => item.id === rule.id);
              onChange([...rules.slice(0, position + 1), { ...rule, id: copy.id },
                ...rules.slice(position + 1)]); }, readOnly)}
          <Popconfirm title={t('graphicDesignLesson.admin.deleteRuleConfirm')}
            onConfirm={() => onChange(rules.filter(item => item.id !== rule.id))}>
            {action(t('graphicDesignLesson.admin.delete'), <DeleteOutlined />,
              () => undefined, readOnly)}
          </Popconfirm>
        </div>) : <p><Typography.Text type="secondary">
          {t('graphicDesignLesson.admin.noRule')}</Typography.Text></p>}
        <Dropdown disabled={readOnly} menu={{ items: allowedTypes.filter(type => type !== 'timeMax')
          .map(type => ({ key: type, label: <div><strong>{RULE_TYPE_LABELS_FR[type]}</strong>
            <div><Typography.Text type="secondary">{RULE_TYPE_HELP_FR[type]}</Typography.Text></div>
          </div> })), onClick: ({ key }) => add(key as TGDRuleType, tier) }}>
          <Button type="dashed" disabled={readOnly}>{t('graphicDesignLesson.admin.add')}</Button>
        </Dropdown>
      </section>;
    })}
    <Drawer open={!!current} title={current?.label[language] || current?.label.fr}
      onClose={() => setSelected(null)} width={520} destroyOnClose>
      {current && <Form layout="vertical">
        <Form.Item label={t('graphicDesignLesson.admin.reads')}><LocalizedInput
          value={current.label} language={language} readOnly={readOnly}
          onChange={label => replace({ ...current, label })} /></Form.Item>
        <Form.Item label={t('graphicDesignLesson.admin.checked')}>
          <Typography.Text type="secondary">{RULE_TYPE_HELP_FR[current.type]}</Typography.Text>
        </Form.Item>
        <RuleParamsForm rule={current} onChange={replace} solution={solution}
          zoneIds={zoneIds} versionIds={versionIds} choiceIds={choiceIds} readOnly={readOnly} />
        <Form.Item label={t('graphicDesignLesson.admin.failMessage')}
          extra={t('graphicDesignLesson.admin.failHelp')}><LocalizedInput multiline
          value={current.failMessage} language={language} readOnly={readOnly}
          onChange={failMessage => replace({ ...current, failMessage })} /></Form.Item>
        <Form.Item label={t('graphicDesignLesson.admin.star')}><Segmented disabled={readOnly}
          value={current.tier} options={tiers.map(value => ({ value,
            label: t(`graphicDesignLesson.admin.tiers.${value}`) }))}
          onChange={tier => replace({ ...current, tier: tier as TGDTier })} /></Form.Item>
      </Form>}
    </Drawer>
  </>;
};
export default RulesEditor;
